import React, { useEffect, useState } from "react";
import { Box, Button, Chip, CircularProgress, Typography } from "@mui/material";
import ThumbsUpDownOutlinedIcon from "@mui/icons-material/ThumbsUpDownOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { makeStyles } from "tss-react/mui";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import SettingsCard from "../Settings/SettingsCard";
import SatisfactionSurveyModal from "../SatisfactionSurveyModal";
import BusinessHoursCard from "./BusinessHoursCard";

const t = (key, options) => i18n.t(`settings.attention.survey.${key}`, options);

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    width: "100%",
  },
  summary: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
  },
  summaryLine: {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  statusChip: {
    alignSelf: "flex-start",
    fontWeight: 600,
  },
}));

const AttentionSettings = () => {
  const { classes } = useStyles();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/satisfaction-survey");
        setSurvey(data);
      } catch (err) {
        toastError(err);
      }
      setLoading(false);
    };
    load();
  }, []);

  const mode = survey?.mode || "disabled";
  const config = survey?.config;
  const companyWide = mode === "company";
  const commentRates = (config?.commentRules || []).flatMap((rule) => rule.rates).sort((a, b) => a - b);

  return (
    <div className={classes.root}>
      <SettingsCard
        icon={ThumbsUpDownOutlinedIcon}
        title={t("cardTitle")}
        subtitle={t("cardSubtitle")}
        action={
          <Button
            size="small"
            variant="outlined"
            startIcon={<TuneOutlinedIcon />}
            disabled={loading || !survey}
            onClick={() => setModalOpen(true)}
          >
            {t("configure")}
          </Button>
        }
      >
        {loading ? (
          <Box display="flex" justifyContent="center" py={1}>
            <CircularProgress size={22} />
          </Box>
        ) : (
          <div className={classes.summary}>
            <Chip
              size="small"
              className={classes.statusChip}
              color={mode !== "disabled" ? "success" : "default"}
              label={t(`status.${mode}`)}
            />
            {companyWide && config?.interactiveList && (
              <Typography className={classes.summaryLine}>{t("summary.interactive")}</Typography>
            )}
            {companyWide && config?.askComment && commentRates.length > 0 && (
              <Typography className={classes.summaryLine}>
                {t("summary.comment", { rates: commentRates.join(", ") })}
              </Typography>
            )}
            {companyWide && config?.customThanks && config.thanksRules.length > 0 && (
              <Typography className={classes.summaryLine}>
                {t("summary.customThanks", { count: config.thanksRules.length })}
              </Typography>
            )}
          </div>
        )}
      </SettingsCard>

      <BusinessHoursCard />

      <SatisfactionSurveyModal
        open={modalOpen}
        survey={survey}
        onClose={() => setModalOpen(false)}
        onSaved={setSurvey}
      />
    </div>
  );
};

export default AttentionSettings;
